if (!customElements.get('customer-review-showcase')) {
  customElements.define(
    'customer-review-showcase',
    class CustomerReviewShowcase extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-review-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-review-dot]'));
        this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-selected')));
        this.autoplay = this.dataset.autoplay === 'true';
        this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '6000', 10);
        this.isDragging = false;
        this.hasDragged = false;
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            this.select(parseInt(dot.dataset.index || '0', 10));
            this.restartAutoplay();
          });
        });

        this.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.target.dataset.blockId);
          if (index >= 0) {
            this.select(index);
            this.stopAutoplay();
          }
        });

        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('click', this.onClickCapture, true);
        this.select(this.currentIndex);
        this.setupReveal();
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.revealObserver?.disconnect();
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('click', this.onClickCapture, true);
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }

      select(index) {
        if (!this.slides.length) {
          return;
        }

        this.currentIndex = (index + this.slides.length) % this.slides.length;

        this.slides.forEach((slide, slideIndex) => {
          const isSelected = slideIndex === this.currentIndex;
          slide.hidden = !isSelected;
          slide.classList.toggle('is-selected', isSelected);
        });

        this.dots.forEach((dot, dotIndex) => {
          const isSelected = dotIndex === this.currentIndex;
          dot.classList.toggle('is-selected', isSelected);
          dot.toggleAttribute('aria-current', isSelected);
        });
      }

      startAutoplay() {
        if (!this.autoplay || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }

        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.select(this.currentIndex + 1), this.autoplaySpeed);
      }

      stopAutoplay() {
        if (this.autoplayTimer) {
          window.clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
      }

      onPointerDown(event) {
        if (!window.matchMedia('(max-width: 740px)').matches || this.slides.length < 2) {
          return;
        }

        if (event.button !== undefined && event.button !== 0) {
          return;
        }

        const interactive = event.target.closest('a, button, input, select, textarea, label');
        if (interactive) {
          return;
        }

        this.isDragging = true;
        this.hasDragged = false;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.stopAutoplay();
        this.classList.add('is-dragging');
        window.addEventListener('pointermove', this.onPointerMove, { passive: false });
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.isDragging) {
          return;
        }

        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;

        if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.hasDragged = true;
          event.preventDefault();
        }
      }

      onPointerUp(event) {
        if (!this.isDragging) {
          return;
        }

        const deltaX = event.clientX - this.startX;
        this.isDragging = false;
        this.classList.remove('is-dragging');
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);

        if (this.hasDragged && Math.abs(deltaX) > 48) {
          this.select(this.currentIndex + (deltaX < 0 ? 1 : -1));
        }

        this.restartAutoplay();
      }

      onClickCapture(event) {
        if (!this.hasDragged) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.hasDragged = false;
      }

      setupReveal() {
        if (this.dataset.revealBound === 'true') {
          return;
        }

        this.dataset.revealBound = 'true';
        this.classList.add('is-reveal-ready');

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
          this.classList.add('is-visible');
          return;
        }

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.classList.add('is-visible');
                this.revealObserver?.disconnect();
              }
            });
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.16 }
        );
        this.revealObserver.observe(this);
      }
    }
  );
}
